/** The Classic / Deep setup choices (docs/spec/07 §2): players, start cave, level. Pure. */

import { findCave, START_CAVES } from '../../caves';
import type { CaveSet } from '../../caves/types';
import { wrapIndex } from '../kit/menuModel';

export interface SetupChoice {
  readonly players: 1 | 2;
  readonly cave: string;
  readonly level: number;
}

export type SetupRow = 'players' | 'cave' | 'level' | 'start';
export const SETUP_ROWS: readonly SetupRow[] = ['players', 'cave', 'level', 'start'];

export const DEFAULT_CHOICE: SetupChoice = { players: 1, cave: 'A', level: 1 };

/** The start caves A E I M, and whether this set has each yet. */
export function startCaves(set: CaveSet): { letter: string; available: boolean }[] {
  return [...START_CAVES].map((letter) => ({ letter, available: findCave(set, letter) !== undefined }));
}

/** The values a row offers, as labels. */
export function rowValues(row: SetupRow, set: CaveSet): { label: string; available: boolean }[] {
  if (row === 'players') return [{ label: '1', available: true }, { label: '2', available: true }];
  if (row === 'cave') return startCaves(set).map((c) => ({ label: c.letter, available: c.available }));
  if (row === 'level') return [1, 2, 3, 4, 5].map((n) => ({ label: String(n), available: true }));
  return [];
}

/** Which value of the row is chosen. */
export function chosenIndex(row: SetupRow, c: SetupChoice): number {
  if (row === 'players') return c.players - 1;
  if (row === 'cave') return Math.max(0, START_CAVES.indexOf(c.cave));
  if (row === 'level') return c.level - 1;
  return -1;
}

/** Choose value `i` of the row, if it is available. */
export function chooseValue(row: SetupRow, c: SetupChoice, i: number, set: CaveSet): SetupChoice {
  const v = rowValues(row, set)[i];
  if (!v || !v.available) return c;
  if (row === 'players') return { ...c, players: i === 1 ? 2 : 1 };
  if (row === 'cave') return { ...c, cave: v.label };
  if (row === 'level') return { ...c, level: i + 1 };
  return c;
}

/**
 * Left/right on a row: players and caves wrap round (skipping caves the set lacks), the level
 * stops at 1 and 5.
 */
export function stepChoice(row: SetupRow, c: SetupChoice, delta: number, set: CaveSet): SetupChoice {
  if (row === 'level') return { ...c, level: Math.max(1, Math.min(5, c.level + delta)) };
  const values = rowValues(row, set);
  if (values.length === 0) return c;
  let i = chosenIndex(row, c);
  for (let k = 0; k < values.length; k++) {
    i = wrapIndex(i + (delta >= 0 ? 1 : -1), values.length);
    if (values[i]?.available === true) return chooseValue(row, c, i, set);
  }
  return c;
}

/** A remembered choice made valid for the set (a cave it lacks falls back to A). */
export function validChoice(c: SetupChoice, set: CaveSet): SetupChoice {
  return findCave(set, c.cave) ? c : { ...c, cave: 'A' };
}
